import { useState, type FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api, setAccessToken } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { isNative } from '../../capacitorInit';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className={`min-h-screen bg-[#F8F9FF] flex ${isNative ? 'items-start overflow-y-auto pt-12' : 'items-center'} justify-center p-4`}>
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8 text-center">
          <h1 className="text-3xl font-serif font-bold text-indigo-700 mb-4">EventsCarousel</h1>
          <p className="text-red-600 text-sm mb-4">Invalid or missing reset link. Please request a new one.</p>
          <Link to="/forgot-password" className="text-indigo-600 text-sm font-medium hover:underline">Request a new link</Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });
      if (data.mfaEnabled) {
        // Resetting via an emailed link only proves inbox access, not the
        // second factor — send MFA users to the normal login flow instead
        // of auto-signing them in.
        navigate('/login', { replace: true });
        return;
      }
      setAccessToken(data.accessToken);
      await refresh();
      navigate('/events');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to reset password. The link may have expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`min-h-screen bg-[#F8F9FF] flex ${isNative ? 'items-start overflow-y-auto pt-12' : 'items-center'} justify-center p-4`}>
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8">
        <h1 className="text-3xl font-serif font-bold text-indigo-700 mb-2">EventsCarousel</h1>
        <p className="text-stone-500 mb-8 text-sm">Choose a new password for your account.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoFocus
              minLength={8}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
